import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// @mui
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Stack,
  Typography,
  Badge,
  ButtonBase,
  SwipeableDrawer,
  TextField,
  InputAdornment,
  alpha,
  useMediaQuery,
} from '@mui/material';
// hooks / utils / guards
import useAuth from '../../hooks/useAuth';
import axios from '../../utils/axios';
import { isItemVisible } from '../../guards/checkNavAccess';
// config
import { PATH_ADMIN } from '../../routes/paths';
import { adminNavConfig } from './navbar/NavConfig';
// components
import Iconify from '../../components/Iconify';

// ----------------------------------------------------------------------
// Phone-only, permission-driven bottom nav for org (admin) users.
//
// Every candidate item carries the SAME gate shape the sidebar uses
// (roles / permission / slug), evaluated by isItemVisible() — the single
// source of truth in guards/checkNavAccess. So this bar can never surface a
// destination the user can't reach, and it adapts per role automatically.
// ----------------------------------------------------------------------

const BAR_HEIGHT = 60; // dock height (floats above the safe area)

// Curated corner candidates (compact labels/icons tuned for a 5-slot bar).
// Icons: Solar bold-duotone — the two-tone weight reads modern and gives the
// dock visual depth that flat single-tone glyphs don't.
const BAR_ITEMS = {
  dashboard: { key: 'dashboard', label: 'Home', icon: 'solar:home-smile-bold-duotone', path: PATH_ADMIN.dashboard, gate: { roles: ['org'], permission: 'dashboard.view' } },
  checkins: { key: 'checkins', label: 'Check-in', icon: 'solar:login-3-bold-duotone', path: PATH_ADMIN.checkIns, gate: { roles: ['org'], permission: 'checkins.view', slug: 'checkin-management' } },
  meeting: { key: 'meeting', label: 'Rooms', icon: 'solar:videocamera-record-bold-duotone', path: PATH_ADMIN.meetingRoomManagement.root, gate: { roles: ['org'], permission: 'meeting_rooms.view', slug: 'meeting-room' } },
  service: { key: 'service', label: 'Tickets', icon: 'solar:ticket-bold-duotone', path: PATH_ADMIN.serviceRequests.home, gate: { roles: ['org'], permission: 'service_requests.view', slug: 'helpdesk' }, badge: 'service' },
  visitors: { key: 'visitors', label: 'Visitors', icon: 'solar:users-group-two-rounded-bold-duotone', path: PATH_ADMIN.visitorManagement, gate: { roles: ['org'], permission: 'visitors.view' } },
  leads: { key: 'leads', label: 'Leads', icon: 'solar:target-bold-duotone', path: PATH_ADMIN.leadsManagement.myDay, gate: { roles: ['org'], permission: 'leads.view', slug: 'leads-management' } },
  chatbots: { key: 'chatbots', label: 'Chats', icon: 'solar:chat-round-dots-bold-duotone', path: PATH_ADMIN.chatbots.dashboard, gate: { roles: ['org'], permission: 'leads.view', slug: 'leads-chatbot' } },
  outreach: { key: 'outreach', label: 'Outreach', icon: 'solar:letter-bold-duotone', path: PATH_ADMIN.outreach.dashboard, gate: { roles: ['org'], permission: 'settings.manage', slug: 'outreach' } },
  members: { key: 'members', label: 'Members', icon: 'solar:users-group-rounded-bold-duotone', path: PATH_ADMIN.members.membershipsPage, gate: { roles: ['org'], permission: 'members.view', slug: 'plans' } },
  invoices: { key: 'invoices', label: 'Invoices', icon: 'solar:bill-list-bold-duotone', path: PATH_ADMIN.orderManagement.invoiceList, gate: { roles: ['org'], permission: 'billing.view', slug: 'orders' } },
  payments: { key: 'payments', label: 'Payments', icon: 'solar:wallet-money-bold-duotone', path: PATH_ADMIN.orderManagement.paymentManagement, gate: { roles: ['org'], permission: 'financials.view', slug: 'orders' } },
  reports: { key: 'reports', label: 'Reports', icon: 'solar:chart-2-bold-duotone', path: PATH_ADMIN.reports.root, gate: { roles: ['org'], permission: 'reports.view', slug: 'reports' } },
  expenses: { key: 'expenses', label: 'Expenses', icon: 'solar:bill-check-bold-duotone', path: PATH_ADMIN.expenses.root, gate: { roles: ['org'], permission: 'expenses.view', slug: 'expenses' }, badge: 'expenses' },
  // Ungated on purpose, like the pages themselves. A community manager or a
  // housekeeping supervisor opening the Hub holds none of the codes above, so
  // every corner is gated off and the bar comes up empty. Their own attendance
  // is the one admin page they are always entitled to, so it backstops the bar.
  myattendance: { key: 'myattendance', label: 'My Day', icon: 'solar:clock-circle-bold-duotone', path: PATH_ADMIN.workforce.me, gate: { roles: ['org'], slug: 'workforce' } },
};

// Ordered corner preference per persona (center "Quick" occupies the middle).
const PERSONA_ORDER = {
  admin: ['dashboard', 'invoices', 'reports', 'leads'],
  sales: ['leads', 'chatbots', 'outreach', 'dashboard'],
  frontdesk: ['checkins', 'meeting', 'service', 'visitors'],
  generic: ['dashboard', 'meeting', 'members', 'invoices'],
};

// Used to top up to 4 corners when a persona's preferred items are gated off.
const FALLBACK_ORDER = [
  'dashboard', 'checkins', 'meeting', 'service', 'visitors', 'members',
  'invoices', 'leads', 'reports', 'expenses', 'payments', 'chatbots', 'outreach',
  // Last, so it only ever appears for somebody the rest of the list has
  // nothing for. It is also the only entry here that needs no permission.
  'myattendance',
];

// Quick-create shortcuts (all gated). They deep-link to the page where the
// create flow lives — a global bar can't own every create dialog.
const CREATE_ACTIONS = [
  { label: 'New Order', icon: 'solar:cart-plus-bold-duotone', path: PATH_ADMIN.orderManagement.list, gate: { roles: ['org'], permission: 'billing.view', slug: 'orders' } },
  { label: 'New Lead', icon: 'solar:user-plus-rounded-bold-duotone', path: PATH_ADMIN.leadsManagement.list, gate: { roles: ['org'], permission: 'leads.view', slug: 'leads-management' } },
  { label: 'Check-in', icon: 'solar:login-3-bold-duotone', path: PATH_ADMIN.checkIns, gate: { roles: ['org'], permission: 'checkins.view', slug: 'checkin-management' } },
  { label: 'Book Room', icon: 'solar:calendar-add-bold-duotone', path: PATH_ADMIN.meetingRoomManagement.root, gate: { roles: ['org'], permission: 'meeting_rooms.view', slug: 'meeting-room' } },
  { label: 'Ask Dex', icon: 'solar:magic-stick-3-bold-duotone', path: PATH_ADMIN.dex, gate: { roles: ['org'], permission: 'dashboard.view', slug: 'dex' } },
];

// Exported so other phone-only surfaces (e.g. the mobile dashboard) rank
// content with the SAME persona the bottom nav uses.
export const derivePersona = (hasPermission) => {
  if (hasPermission('financials.view') || hasPermission('reports.view')) return 'admin';
  if (hasPermission('leads.view')) return 'sales';
  if (['checkins.view', 'meeting_rooms.view', 'service_requests.view', 'visitors.view'].some((p) => hasPermission(p))) {
    return 'frontdesk';
  }
  return 'generic';
};

// Resolve a navigable path for a nav item (its own, or first visible child's).
const resolvePath = (item, ctx) => {
  if (item.path) return item.path;
  const child = item.children?.find((c) => isItemVisible(c, ctx));
  return child?.path || null;
};

export default function AdminMobileNav() {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [badges, setBadges] = useState({ service: 0, expenses: 0 });

  const isOrg = user?.role === 'org';
  const ctx = useMemo(() => ({ user, hasPermission }), [user, hasPermission]);

  // Best-effort badge counts. Silent-fail: a failed call just means no badge.
  useEffect(() => {
    if (!isOrg) return undefined;
    let active = true;

    (async () => {
      const next = { service: 0, expenses: 0 };
      if (hasPermission('service_requests.view')) {
        try {
          const r = await axios.get('/api/v1/helpdesk/admin/service-dashboard/', { params: { date_range: '7d' } });
          next.service = r?.data?.statistics?.total_tickets?.open_tickets || 0;
        } catch { /* ignore */ }
      }
      // The control center is money end to end and the endpoint asks for
      // financials.view, so anything less than that would only earn a 403.
      if (hasPermission('financials.view')) {
        try {
          const r = await axios.get('/api/v1/expenses/admin/expenses/dashboard/');
          next.expenses = r?.data?.kpis?.pending_count || 0;
        } catch { /* ignore */ }
      }
      if (active) setBadges(next);
    })();

    return () => {
      active = false;
    };
  }, [isOrg, pathname, hasPermission]);

  const gateItem = (key) => {
    const it = BAR_ITEMS[key];
    if (!it) return null;
    return isItemVisible({ ...it.gate, path: it.path }, ctx) ? it : null;
  };

  const corners = useMemo(() => {
    const persona = derivePersona(hasPermission);
    const picked = [];
    const pushKey = (key) => {
      if (picked.length >= 4 || picked.some((c) => c.key === key)) return;
      const it = gateItem(key);
      if (it) picked.push(it);
    };
    PERSONA_ORDER[persona].forEach(pushKey);
    if (picked.length < 4) FALLBACK_ORDER.forEach(pushKey);
    return picked.slice(0, 4);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx]);

  const isActive = (path) => path && (pathname === path || pathname.startsWith(`${path}/`));
  const badgeFor = (item) => (item?.badge ? badges[item.badge] || 0 : 0);

  const go = (path) => {
    setSheetOpen(false);
    setSearch('');
    if (path) navigate(path);
  };

  // Flattened, permission-filtered list of every admin destination for the sheet.
  const allDestinations = useMemo(() => {
    const out = [];
    adminNavConfig.forEach((group) => {
      group.items.forEach((item) => {
        if (!isItemVisible(item, ctx)) return;
        const path = resolvePath(item, ctx);
        if (!path) return;
        out.push({ title: item.title, icon: item.icon, path });
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx]);

  const createActions = useMemo(
    () => CREATE_ACTIONS.filter((a) => isItemVisible({ ...a.gate, path: a.path }, ctx)),
    [ctx]
  );

  const cornerPaths = corners.map((c) => c.path);
  const filteredDestinations = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? allDestinations.filter((d) => d.title.toLowerCase().includes(q))
      : allDestinations.filter((d) => !cornerPaths.includes(d.path));
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, allDestinations, corners]);

  const badgeForPath = (path) => {
    if (path === PATH_ADMIN.serviceRequests.home) return badges.service;
    if (path === PATH_ADMIN.expenses.root) return badges.expenses;
    return 0;
  };

  if (!isPhone || !isOrg || corners.length === 0) return null;

  const left = corners.slice(0, 2);
  const right = corners.slice(2, 4);

  const renderCorner = (item) => {
    const active = isActive(item.path);
    const count = badgeFor(item);
    return (
      <ButtonBase
        key={item.key}
        onClick={() => go(item.path)}
        sx={{
          flex: 1,
          flexDirection: 'column',
          gap: 0.25,
          py: 0.5,
          borderRadius: 2,
          color: active ? 'primary.main' : 'text.secondary',
          transition: 'transform 0.15s',
          '&:active': { transform: 'scale(0.9)' },
        }}
      >
        {/* Active state = soft tinted pill behind the icon (M3 style) */}
        <Box
          sx={{
            px: 1.5,
            py: 0.4,
            borderRadius: 99,
            lineHeight: 0,
            bgcolor: active ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
            transition: 'background-color 0.2s, transform 0.2s',
            transform: active ? 'scale(1.06)' : 'scale(1)',
          }}
        >
          <Badge
            badgeContent={count}
            color="error"
            max={99}
            overlap="circular"
            sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}
          >
            <Iconify icon={item.icon} width={22} height={22} />
          </Badge>
        </Box>
        <Typography variant="caption" sx={{ fontSize: 10.5, fontWeight: active ? 700 : 500, lineHeight: 1 }}>
          {item.label}
        </Typography>
      </ButtonBase>
    );
  };

  return (
    <>
      {/* Floating frosted dock — detached from the screen edges */}
      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 'calc(env(safe-area-inset-bottom) + 12px)',
          zIndex: theme.zIndex.appBar,
          px: 1.5,
          pointerEvents: 'none',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            pointerEvents: 'auto',
            height: BAR_HEIGHT,
            maxWidth: 420,
            mx: 'auto',
            px: 0.75,
            borderRadius: 99,
            bgcolor: alpha(theme.palette.background.paper, 0.55),
            backdropFilter: 'blur(18px) saturate(180%)',
            WebkitBackdropFilter: 'blur(18px) saturate(180%)',
            border: '1px solid',
            borderColor: alpha('#fff', 0.55),
            boxShadow: `0 12px 32px -12px ${alpha(theme.palette.grey[900], 0.28)}, inset 0 1px 0 ${alpha('#fff', 0.6)}`,
          }}
        >
          {left.map(renderCorner)}

          {/* Center — Quick (inline, no overhang) */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <ButtonBase
              onClick={() => setSheetOpen(true)}
              aria-label="Quick actions"
              sx={{
                width: 48,
                height: 48,
                borderRadius: '34%', // squircle, not a plain circle
                color: 'primary.contrastText',
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${theme.palette.primary.light} 130%)`,
                boxShadow: `0 8px 18px -6px ${alpha(theme.palette.primary.main, 0.55)}, inset 0 1px 0 ${alpha('#fff', 0.28)}`,
                transition: 'transform 0.2s',
                '&:active': { transform: 'scale(0.9)' },
              }}
            >
              {/* Plus rotates into a × when the sheet opens */}
              <Iconify
                icon="mdi:plus"
                width={26}
                height={26}
                sx={{
                  transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transform: sheetOpen ? 'rotate(135deg)' : 'rotate(0deg)',
                }}
              />
            </ButtonBase>
          </Box>

          {right.map(renderCorner)}
        </Stack>
      </Box>

      {/* Quick sheet — frosted glass to match the dock */}
      <SwipeableDrawer
        anchor="bottom"
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onOpen={() => setSheetOpen(true)}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            borderTopLeftRadius: 28,
            borderTopRightRadius: 28,
            maxHeight: '80vh',
            pb: 'env(safe-area-inset-bottom)',
            bgcolor: alpha(theme.palette.background.paper, 0.86),
            backdropFilter: 'blur(20px) saturate(160%)',
            WebkitBackdropFilter: 'blur(20px) saturate(160%)',
            backgroundImage: 'none',
            boxShadow: `0 -8px 40px -12px ${alpha(theme.palette.grey[900], 0.25)}, inset 0 1px 0 ${alpha('#fff', 0.6)}`,
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.25, pb: 2 }}>
          {/* grab handle */}
          <Box sx={{ width: 44, height: 5, borderRadius: 3, bgcolor: alpha(theme.palette.text.primary, 0.14), mx: 'auto', mb: 1.75 }} />

          {/* Pill search */}
          <TextField
            size="small"
            fullWidth
            placeholder="Search anything…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="solar:magnifer-linear" width={18} sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 99,
                bgcolor: alpha(theme.palette.grey[500], 0.1),
                '& fieldset': { border: 'none' },
                '&.Mui-focused': {
                  bgcolor: 'background.paper',
                  boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.5)}`,
                },
              },
            }}
          />

          {!search && createActions.length > 0 && (
            <>
              <Typography variant="overline" sx={{ px: 0.5, color: 'text.disabled', letterSpacing: 1.1 }}>
                Create
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  mt: 1, mb: 2, overflowX: 'auto', pb: 0.5, mx: -2, px: 2,
                  scrollSnapType: 'x proximity',
                  '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none',
                }}
              >
                {createActions.map((a) => (
                  <ButtonBase
                    key={a.label}
                    onClick={() => go(a.path)}
                    sx={{
                      flexShrink: 0,
                      flexDirection: 'column',
                      gap: 0.75,
                      px: 1.25,
                      py: 1.25,
                      minWidth: 82,
                      borderRadius: 2.5,
                      scrollSnapAlign: 'start',
                      bgcolor: alpha(theme.palette.grey[500], 0.08),
                      transition: 'transform 0.15s',
                      '&:active': { transform: 'scale(0.94)' },
                    }}
                  >
                    {/* Squircle icon chip — echoes the dock's center button */}
                    <Box
                      sx={{
                        width: 42, height: 42, borderRadius: '34%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                      }}
                    >
                      <Iconify icon={a.icon} width={22} height={22} />
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1, color: 'text.primary' }}>
                      {a.label}
                    </Typography>
                  </ButtonBase>
                ))}
              </Stack>
            </>
          )}

          <Typography variant="overline" sx={{ px: 0.5, color: 'text.disabled', letterSpacing: 1.1 }}>
            {search ? 'Results' : 'Jump to'}
          </Typography>

          <Box
            sx={{
              mt: 1,
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 1,
            }}
          >
            {filteredDestinations.map((d) => {
              const active = isActive(d.path);
              const count = badgeForPath(d.path);
              return (
                <ButtonBase
                  key={d.path + d.title}
                  onClick={() => go(d.path)}
                  sx={{
                    flexDirection: 'column',
                    gap: 0.5,
                    py: 1.25,
                    px: 0.5,
                    borderRadius: 2.5,
                    bgcolor: active
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.grey[500], 0.08),
                    color: active ? 'primary.main' : 'text.primary',
                    transition: 'transform 0.15s',
                    '&:active': { transform: 'scale(0.94)' },
                  }}
                >
                  <Badge
                    badgeContent={count}
                    color="error"
                    max={99}
                    sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 15, minWidth: 15 } }}
                  >
                    {/* Fixed box so percentage-sized icons (SvgIconStyle masked
                        spans, e.g. Inspect) don't collapse to 0 */}
                    <Box
                      sx={{
                        width: 24, height: 24,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        '& svg': { width: 24, height: 24 },
                      }}
                    >
                      {d.icon}
                    </Box>
                  </Badge>
                  <Typography
                    variant="caption"
                    align="center"
                    sx={{ fontSize: 10.5, lineHeight: 1.1, fontWeight: active ? 700 : 500 }}
                  >
                    {d.title}
                  </Typography>
                </ButtonBase>
              );
            })}
          </Box>

          {filteredDestinations.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              Nothing matches “{search}”.
            </Typography>
          )}
        </Box>
      </SwipeableDrawer>
    </>
  );
}
